import React, { useEffect, useState } from "react";
import http from "../../../../services/http";

/**
 * Xem thông tin thuế / pháp lý trạm Việt Nam — Quân 14/10/2026: trước đây Admin không có
 * chỗ xem, trạm khai xong treo "Đã khai · chờ duyệt" mãi. Đọc GET /admin/partners/:id/tax-legal.
 * 04/11/2026 (Quân): bỏ nút Verify — khớp giấy phép đã duyệt thì máy tự xác minh, Admin chỉ xử lý khi
 * lệch: "Use licence values" hoặc PATCH /admin/partners/:id/tax-legal-review { status: "needs_update", note }.
 * Trạm chưa khai (không có legalForm) ⇒ không hiện khối, tránh làm rối trạm nước ngoài.
 */
type Data = {
	legalForm?: "company" | "household" | "individual";
	taxIdentifier?: string;
	registeredLegalName?: string;
	certificateFiles?: string[];
	kycStatus?: "declared" | "verified" | "needs_update";
	declaredAt?: string | null;
	reviewedAt?: string | null;
	reviewNote?: string | null;
	/** 02/11/2026: so từng ô với giấy phép VN đã duyệt (null/không có = chưa có giấy duyệt). */
	licenceCompare?: {
		allMatch: boolean;
		locked: boolean;
		fields: Array<{ field: "legalForm" | "registeredLegalName" | "taxIdentifier"; declared: string | null; licence: string | null; match: boolean | null }>;
	} | null;
	filledFromLicenceAt?: string | null;
	/** 03/11/2026: máy tự Verified vì khai khớp giấy phép đã duyệt. */
	verifiedFromLicence?: boolean;
};

const LOAI: Record<string, string> = {
	company: "Company",
	household: "Household business",
	individual: "Individual",
};

const MAU: Record<string, { bg: string; fg: string; label: string }> = {
	declared: { bg: "#FFFAEB", fg: "#B54708", label: "Waiting for review" },
	verified: { bg: "#ECFDF3", fg: "#067647", label: "Verified" },
	needs_update: { bg: "#FEF3F2", fg: "#B42318", label: "Needs update" },
};

function ngay(v?: string | null) {
	if (!v) return "";
	const d = new Date(v);
	return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-GB");
}

export default function TaxLegalReview(props: { partnerId: string; open: boolean }) {
	const [d, setD] = useState<Data | null>(null);
	const [busy, setBusy] = useState(false);
	const [ghiChu, setGhiChu] = useState("");
	const [moYeuCau, setMoYeuCau] = useState(false);
	const [loi, setLoi] = useState("");

	const tai = async () => {
		try {
			const { data } = await http.get(`/admin/partners/${props.partnerId}/tax-legal`);
			setD(data?.data || null);
		} catch {
			setD(null);
		}
	};
	useEffect(() => {
		if (props.open && props.partnerId) tai();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.open, props.partnerId]);

	const yeuCauSua = async () => {
		if (!ghiChu.trim()) {
			setLoi("Please write what the station must fix — they will see this note.");
			return;
		}
		setBusy(true);
		setLoi("");
		try {
			await http.patch(`/admin/partners/${props.partnerId}/tax-legal-review`, {
				status: "needs_update",
				note: ghiChu.trim(),
			});
			setMoYeuCau(false);
			setGhiChu("");
			await tai();
		} catch (e: any) {
			setLoi(e?.response?.data?.message || "Could not save. Please try again.");
		} finally {
			setBusy(false);
		}
	};

	// 02/11/2026: chép loại hình / tên / MST từ giấy phép đã duyệt (đè dữ liệu trạm đã khai, về "chờ duyệt").
	const dungGiay = async () => {
		if (d?.legalForm && !window.confirm("Replace the station's tax details with the values on the approved licence?")) return;
		setBusy(true);
		setLoi("");
		try {
			await http.post(`/admin/partners/${props.partnerId}/tax-legal/use-licence`);
			await tai();
		} catch (e: any) {
			setLoi(e?.response?.data?.message || "Could not save. Please try again.");
		} finally {
			setBusy(false);
		}
	};

	const so = d?.licenceCompare || null;
	if (!d || (!d.legalForm && !so)) return null;
	// 03/11/2026 (Quân): khớp giấy phép đã duyệt ⇒ chỉ hiện "✓ From licence", không nút. Lệch mới hiện nút.
	const khopGiay = Boolean(so && so.allMatch && d.legalForm);
	// 04/11/2026: máy chép loại hình + tên từ giấy, hộ / cá nhân còn phải tự khai mã số thuế.
	// CLO 04/11: hộ / cá nhân khớp giấy vẫn là "Declared" — mã số thuế tự khai, không ai đối chiếu.
	const thieuMst = Boolean(d.legalForm && !d.taxIdentifier);
	const nhanCho = thieuMst ? "Waiting for tax code" : !so ? "Waiting for licence" : khopGiay ? "Declared" : "Differs from licence";
	const m = d.legalForm
		? d.kycStatus === "verified" && (d.verifiedFromLicence || khopGiay)
			? { ...MAU.verified, label: "✓ From licence" }
			: d.kycStatus === "verified" || d.kycStatus === "needs_update"
			? MAU[d.kycStatus]
			: { ...MAU.declared, label: nhanCho }
		: { bg: "#F2F4F7", fg: "#475467", label: "Not declared yet" };
	const nut = (bg: string): React.CSSProperties => ({
		border: "none",
		borderRadius: 8,
		padding: "7px 14px",
		background: bg,
		color: "#fff",
		fontWeight: 700,
		fontSize: 13,
		cursor: busy ? "wait" : "pointer",
		opacity: busy ? 0.6 : 1,
	});
	const dong = (nhan: string, giaTri?: string, field?: string) => {
		const o = field ? so?.fields.find((x) => x.field === field) : undefined;
		const hienGiay = (v: string | null) => (field === "legalForm" && v ? LOAI[v] || v : v);
		return (
			<div style={{ display: "flex", gap: 8, fontSize: 13, lineHeight: 1.6 }}>
				<span style={{ width: 170, color: "#667085", flex: "0 0 auto" }}>{nhan}</span>
				<span style={{ color: "#101828", fontWeight: 600, wordBreak: "break-word" }}>
					{giaTri || "—"}
					{o?.match === true ? <span data-testid={`tax-match-${field}`} style={{ color: "#067647", fontWeight: 600 }}> · Matches licence ✓</span> : null}
					{o?.match === false ? (
						<span data-testid={`tax-diff-${field}`} style={{ color: "#B42318", fontWeight: 600 }}>
							{" "}
							· Licence: {hienGiay(o.licence)}
						</span>
					) : null}
				</span>
			</div>
		);
	};

	return (
		<div
			data-testid="tax-legal-review"
			style={{ border: "1px solid #D0D5DD", borderRadius: 10, padding: "14px 16px", marginBottom: 16, background: "#fff" }}>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
				<div style={{ fontWeight: 700, fontSize: 14, color: "#101828" }}>Tax & Legal (Vietnam)</div>
				<span style={{ background: m.bg, color: m.fg, borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>
					{m.label}
				</span>
			</div>
			{dong("Legal form", d.legalForm ? LOAI[d.legalForm] || d.legalForm : "", "legalForm")}
			{dong("Tax code / Citizen ID", d.taxIdentifier, "taxIdentifier")}
			{dong("Registered legal name", d.registeredLegalName, "registeredLegalName")}
			{d.legalForm ? dong("Declared on", ngay(d.declaredAt)) : null}
			{d.filledFromLicenceAt ? dong("Filled from licence", ngay(d.filledFromLicenceAt)) : null}
			{d.reviewedAt ? dong("Last reviewed", ngay(d.reviewedAt)) : null}
			{d.kycStatus === "needs_update" && d.reviewNote ? dong("Note sent to station", d.reviewNote) : null}
			<p style={{ margin: "8px 0 10px", fontSize: 12.5, color: "#475467", lineHeight: 1.45 }}>
				{so
					? khopGiay
						? so.locked
							? "All fields match the approved licence."
							: "Legal form and name match the approved licence. The tax code is declared by the station."
						: "Compared with the approved licence above. Use the licence values, or ask the station to update."
					: "Checked automatically when the business licence is approved."}
			</p>
			{!so || khopGiay ? null : (
			<div data-testid="tax-actions" style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
				<button type="button" data-testid="tax-use-licence" disabled={busy} style={nut("#0F5F9E")} onClick={dungGiay}>
					Use licence values
				</button>
				{!d.legalForm ? null : !moYeuCau ? (
					<button type="button" disabled={busy} style={nut(d.kycStatus === "verified" ? "#98A2B3" : "#B42318")} onClick={() => setMoYeuCau(true)}>
						Ask station to update…
					</button>
				) : (
					<>
						<input
							value={ghiChu}
							onChange={(e) => setGhiChu(e.target.value)}
							placeholder="What the station must fix (e.g. tax code does not match the licence)"
							style={{ flex: "1 1 260px", padding: "7px 10px", border: "1px solid #D0D5DD", borderRadius: 8, fontSize: 13 }}
						/>
						<button type="button" disabled={busy} style={nut("#B42318")} onClick={yeuCauSua}>
							Send
						</button>
					</>
				)}
			</div>
			)}
			{loi ? <p style={{ margin: "8px 0 0", color: "#B42318", fontSize: 12.5 }}>{loi}</p> : null}
		</div>
	);
}
